// Secure session storage utilities for cookie-based auth
// User session is now managed via HTTP-only cookies on the server
// This file provides client-side caching of user metadata only

// Simple user metadata cache (non-sensitive data only)
class UserMetadataCache {
  constructor() {
    this.USER_KEY = 'saydo_user_meta';
  }

  // Cache user metadata for quick access (no tokens)
  setUserData(user) {
    try {
      if (!user) {
        this.clearUserData();
        return false;
      }

      // Only store minimal, non-sensitive user metadata
      const userData = {
        id: user.id,
        email: user.email,
        emailVerified: user.email_confirmed_at || user.emailVerified,
        createdAt: user.created_at || user.createdAt,
        lastSignIn: user.last_sign_in_at || user.lastSignIn,
        cachedAt: Date.now()
      };

      // Use sessionStorage for temporary caching
      sessionStorage.setItem(this.USER_KEY, JSON.stringify(userData));
      return true;
    } catch (error) {
      console.warn('Error caching user data:', error);
      return false;
    }
  }

  // Get cached user metadata
  getUserData() {
    try {
      const data = sessionStorage.getItem(this.USER_KEY);
      if (!data) return null;

      const userData = JSON.parse(data);

      // Cache is valid for 5 minutes max
      const cacheAge = Date.now() - (userData.cachedAt || 0);
      if (cacheAge > 5 * 60 * 1000) {
        this.clearUserData();
        return null;
      }

      return userData;
    } catch (error) {
      console.warn('Error retrieving user data:', error);
      return null;
    }
  }

  // Clear cached user data
  clearUserData() {
    try {
      sessionStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.warn('Error clearing user data:', error);
    }
  }

  // Check if we have cached user data
  hasCachedUser() {
    return this.getUserData() !== null;
  }

  // Get user ID from cache
  getUserId() {
    const userData = this.getUserData();
    return userData?.id || null;
  }

  // Get user email from cache
  getUserEmail() {
    const userData = this.getUserData();
    return userData?.email || null;
  }
}

export const userCache = new UserMetadataCache();

// Session manager - simplified for cookie-based auth
export class SessionManager {
  constructor() {
    this.user = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      // Check for cached user metadata
      const userData = userCache.getUserData();
      if (userData) {
        this.user = userData;
      }
      this.isInitialized = true;
    } catch (error) {
      console.warn('Session initialization error:', error);
      this.isInitialized = true;
    }
  }

  // Set user from server response
  setUser(user) {
    if (!user) {
      this.clearSession();
      return;
    }

    this.user = user;
    userCache.setUserData(user);
  }

  // Alias for backward compatibility
  setSession(session) {
    if (session?.user) {
      this.setUser(session.user);
    } else if (session) {
      this.setUser(session);
    } else {
      this.clearSession();
    }
  }

  getUser() {
    return this.user;
  }

  getSession() {
    return this.user ? { user: this.user } : null;
  }

  clearSession() {
    this.user = null;
    userCache.clearUserData();
  }

  isAuthenticated() {
    return this.user !== null;
  }
}

export const sessionManager = new SessionManager();

// Legacy exports for backward compatibility
export const secureStorage = {
  setUserSession: (user) => userCache.setUserData(user),
  getUserSession: () => userCache.getUserData(),
  clearUserSession: () => userCache.clearUserData(),
  isAuthenticated: () => userCache.hasCachedUser(),
  getUserId: () => userCache.getUserId(),
  getUserEmail: () => userCache.getUserEmail()
};

// Token store is no longer needed - tokens are in HTTP-only cookies
// Keep a dummy export for backward compatibility
export const tokenStore = {
  setTokens: () => { },
  getAccessToken: () => null,
  getRefreshToken: () => null,
  getExpiresAt: () => null,
  isValid: () => false,
  clear: () => { }
};

// Clear legacy localStorage data
export const clearLegacyStorage = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('saydo_') ||
        key.includes('supabase.auth') ||
        key.includes('supabase.anon')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log('Cleared legacy localStorage data');
    }
  } catch (error) {
    console.warn('Error clearing legacy storage:', error);
  }
};

// Check session health - now just checks if user is cached
export const checkSessionHealth = () => {
  const user = userCache.getUserData();
  return {
    hasUserMetadata: !!user,
    hasValidToken: false, // Tokens are server-side now
    tokenExpiresAt: null,
    isFullyAuthenticated: !!user // Will be validated on next server call
  };
};